/**
 * Analytics aggregation — pure functions over live Frappe records.
 *
 * ---- Why this exists on the client at all ----
 *
 * `CLAUDE.md`'s FE/BE split says cross-requirement analytics aggregates belong to the backend, and
 * it is right. The three methods that would serve them (`analytics.get_funnel`, `get_workload`,
 * `get_conversion_breakdown`) are filed as asks E14-E16 and do NOT exist on the live server —
 * verified 2026-08-09, HTTP 417 "Failed to get method for command". The alternative to aggregating
 * here was a screen that shows nothing until the Frappe team ships them.
 *
 * So this is a deliberate, bounded departure, and it is written to be DELETED rather than
 * maintained: when E14-E16 land, the hooks swap to those methods and this file goes away. Nothing
 * else in the app imports it.
 *
 * **Scaling ceiling.** Every function here is O(enquiries + lines + history) over payloads the
 * screen has already fetched whole. At the live scale measured 2026-08-09 — 23 enquiries, 159 line
 * rows, 119 version rows, ~50KB total — that is nothing. At tens of thousands of enquiries it is
 * wrong, and the fix is E14-E16, not optimisation here.
 *
 * Everything is pure: same inputs, same outputs, no clock, no fetch. That is what makes the
 * arithmetic testable, which matters more than usual given the whole point is numbers a purchase
 * manager will act on.
 */

import type {
  ConversionBreakdown,
  CustomerConversion,
  EnquiryFact,
  Funnel,
  FunnelStage,
  PurchaserWorkload,
  SizeBucket,
  StatusTransition,
  TypeConversion,
  Workload,
} from './types'

/**
 * The funnel's progression, in the LIVE status vocabulary (read off
 * `get_pipeline_analytics.by_stage` on rfqdev, 2026-08-09).
 *
 * `Rejected` and `Archived` are deliberately absent: they are terminal EXITS, not steps. An
 * enquiry leaves the funnel through them, it does not advance to them — and counting them as a
 * stage would put the losses at the bottom of the funnel as though they were wins.
 */
export const FUNNEL_STAGES = [
  'Draft',
  'Open',
  'RFQ Sent',
  'Quotes Received',
  'Comparison Done',
  'Quotation Sent',
  'Won',
] as const

export type FunnelStageName = (typeof FUNNEL_STAGES)[number]

const STAGE_INDEX = new Map<string, number>(FUNNEL_STAGES.map((s, i) => [s, i]))

/** Round to `dp` decimals, returning a number rather than a string. */
function round(n: number, dp = 2): number {
  return Number(n.toFixed(dp))
}

/** A rate in 0..1, or 0 when the denominator is empty. Never NaN, never Infinity. */
function rate(numerator: number, denominator: number): number {
  return denominator > 0 ? round(numerator / denominator) : 0
}

/* ------------------------------------------------------------------ *
 * Headline KPIs
 * ------------------------------------------------------------------ */

/** Terminal statuses — an enquiry in one of these is no longer in flight. */
const TERMINAL_STATUSES = new Set(['Won', 'Rejected', 'Archived'])

export interface Kpis {
  total: number
  won: number
  /** Enquiries that reached a decision — won or rejected. The denominator of `win_rate`. */
  decided: number
  open: number
  /** `won / total`, 0..1. */
  conversion_rate: number
  /** `won / decided`, 0..1 — or null when nothing has been decided yet, which is not the same
   *  as 0% and must not render as it. */
  win_rate: number | null
  /** Mean days to the last status change, over enquiries that have a usable one. Null when none
   *  do — again, not 0. */
  avg_cycle_days: number | null
}

/**
 * The four headline numbers, derived from the same facts every other board uses.
 *
 * ---- Why these are not taken from the server ----
 *
 * `analytics.get_pipeline_analytics` returns exactly these aggregates, and we used to render them
 * straight. But its date filtering is broken: **`from_date` is ignored whenever `to_date` is also
 * supplied** (measured on rfqdev 2026-08-11 — `from_date=2026-08-11&to_date=2026-08-11` returns all
 * 24 enquiries where the truth is 1; each bound alone is applied correctly). Every range preset
 * sends both bounds, so the server's tiles answered "all time" no matter which range was picked,
 * while the funnel and workload below them answered the range correctly. Two contradictory sets of
 * numbers on one screen is worse than either.
 *
 * Deriving them here makes every number on the page come from one filtered set of facts, so they
 * agree by construction. When the server's range filter is fixed this can go back to the method —
 * the definitions below are deliberately the same ones it uses.
 */
export function buildKpis(enquiries: readonly EnquiryFact[]): Kpis {
  const won = enquiries.filter((e) => e.status === 'Won').length
  const rejected = enquiries.filter((e) => e.status === 'Rejected').length
  const decided = won + rejected
  const open = enquiries.filter((e) => !TERMINAL_STATUSES.has(e.status)).length
  const cycles = enquiries.map((e) => e.cycle_days).filter((d): d is number => d !== null)

  return {
    total: enquiries.length,
    won,
    decided,
    open,
    conversion_rate: rate(won, enquiries.length),
    win_rate: decided > 0 ? rate(won, decided) : null,
    avg_cycle_days:
      cycles.length > 0 ? round(cycles.reduce((s, d) => s + d, 0) / cycles.length, 1) : null,
  }
}

/* ------------------------------------------------------------------ *
 * Funnel
 * ------------------------------------------------------------------ */

/**
 * How far each enquiry ever got, as an index into `FUNNEL_STAGES` (-1 = never reached any stage).
 *
 * This is the crux of the whole funnel. The current status alone cannot answer it: an enquiry
 * REJECTED at `Quotes Received` now reads `Rejected`, and the document no longer says how far it
 * got — which is exactly the drop-off a funnel exists to measure. So history wins where it exists,
 * and the current status is a floor beneath it (an enquiry sitting in `RFQ Sent` reached `RFQ
 * Sent` whether or not anyone recorded the transition).
 */
export function furthestStageReached(
  enquiries: readonly EnquiryFact[],
  transitions: readonly StatusTransition[],
): Map<string, number> {
  const byEnquiry = new Map<string, number>()

  for (const t of transitions) {
    const i = STAGE_INDEX.get(t.to_status)
    if (i === undefined) continue // a transition INTO Rejected/Archived says nothing about progress
    const current = byEnquiry.get(t.enquiry)
    if (current === undefined || i > current) byEnquiry.set(t.enquiry, i)
  }

  const furthest = new Map<string, number>()
  for (const e of enquiries) {
    const fromHistory = byEnquiry.get(e.id) ?? -1
    const fromStatus = STAGE_INDEX.get(e.status) ?? -1
    furthest.set(e.id, Math.max(fromHistory, fromStatus))
  }
  return furthest
}

/**
 * Cumulative reach per stage, plus the line count carried to each.
 *
 * `reached` is "ever got at least this far", NOT "is sitting here now" — the latter is
 * `PipelineAnalytics.by_stage`, and feeding it into a funnel produces meaningless drop-off. The
 * result is monotonically non-increasing by construction.
 *
 * No `value`: `Enquiry.total` is 0.00 on every live enquiry (verified 2026-08-09), so a money
 * funnel would be a row of honest zeros pretending to be a measurement.
 */
export function buildFunnel(
  enquiries: readonly EnquiryFact[],
  transitions: readonly StatusTransition[],
): Funnel {
  const furthest = furthestStageReached(enquiries, transitions)

  const stages: FunnelStage[] = FUNNEL_STAGES.map((stage, i) => {
    const reachedHere = enquiries.filter((e) => (furthest.get(e.id) ?? -1) >= i)
    return {
      stage,
      reached: reachedHere.length,
      lines: reachedHere.reduce((sum, e) => sum + e.lines, 0),
    }
  })

  return { stages, stage_avg_days: averageStageDays(transitions) }
}

/**
 * Average days for each consecutive transition, keyed `"From→To"`.
 *
 * Only transitions between two funnel stages are timed. A step into `Rejected` has no meaningful
 * duration to average into a progression, and including it would make the slowest "stage" a death.
 */
export function averageStageDays(transitions: readonly StatusTransition[]): Record<string, number> {
  const byEnquiry = new Map<string, StatusTransition[]>()
  for (const t of transitions) {
    const list = byEnquiry.get(t.enquiry)
    if (list) list.push(t)
    else byEnquiry.set(t.enquiry, [t])
  }

  const durations = new Map<string, number[]>()
  for (const list of byEnquiry.values()) {
    const ordered = [...list].sort((a, b) => a.changed_at.localeCompare(b.changed_at))
    for (let i = 1; i < ordered.length; i++) {
      const from = ordered[i - 1]
      const to = ordered[i]
      if (!STAGE_INDEX.has(from.to_status) || !STAGE_INDEX.has(to.to_status)) continue
      const days =
        (new Date(to.changed_at).getTime() - new Date(from.changed_at).getTime()) / 86_400_000
      if (!Number.isFinite(days) || days < 0) continue
      const key = `${from.to_status}→${to.to_status}`
      const bucket = durations.get(key)
      if (bucket) bucket.push(days)
      else durations.set(key, [days])
    }
  }

  const out: Record<string, number> = {}
  for (const [key, list] of durations) {
    out[key] = round(list.reduce((s, d) => s + d, 0) / list.length, 1)
  }
  return out
}

/** The slowest transition — selection among returned values, not a new derivation. */
export function bottleneckKey(stageAvgDays: Record<string, number>): string | null {
  let key: string | null = null
  let worst = -Infinity
  for (const [k, v] of Object.entries(stageAvgDays)) {
    if (v > worst) {
      worst = v
      key = k
    }
  }
  return key
}

/**
 * The share of `from` lost by the next stage, 0..1.
 *
 * Null when `from` is 0: a drop-off out of an empty stage is undefined, and printing "0% lost"
 * would read as a healthy step rather than an absent one.
 */
export function dropOffPct(from: number, to: number): number | null {
  if (from <= 0) return null
  return Math.max(0, (from - to) / from)
}

/* ------------------------------------------------------------------ *
 * Workload
 * ------------------------------------------------------------------ */

/** Statuses meaning "still in flight" — everything on the funnel except the terminal `Won`. */
const OPEN_STATUSES = new Set<string>([
  'Draft',
  'Open',
  'RFQ Sent',
  'Quotes Received',
  'Comparison Done',
  'Quotation Sent',
])

/**
 * Per-purchaser workload, weighted by line items.
 *
 * The reason this board exists: on live, `ENQ-2026-00024` carries 130 lines while the other 22
 * enquiries carry 2 or fewer — one enquiry is 82% of every line item on the system. A table that
 * counts enquiries says those purchasers did comparable work. They did not.
 *
 * Rows are keyed on `purchaser`, the Frappe user id, never on a display name — two people sharing
 * a display name would silently merge into one row of a performance table.
 *
 * Sorted by `lines` descending: the default that puts the skew in front of you.
 */
export function buildWorkload(enquiries: readonly EnquiryFact[]): Workload {
  const groups = new Map<string, EnquiryFact[]>()
  for (const e of enquiries) {
    const list = groups.get(e.purchaser)
    if (list) list.push(e)
    else groups.set(e.purchaser, [e])
  }

  const purchasers: PurchaserWorkload[] = [...groups].map(([purchaser, rows]) => {
    const won = rows.filter((e) => e.status === 'Won')
    const open = rows.filter((e) => OPEN_STATUSES.has(e.status))
    const lines = rows.reduce((s, e) => s + e.lines, 0)
    const linesWon = won.reduce((s, e) => s + e.lines, 0)
    const cycles = rows.map((e) => e.cycle_days).filter((d): d is number => d !== null)

    return {
      purchaser,
      purchaser_name: rows[0].purchaser_name || purchaser,
      enquiries: rows.length,
      lines,
      avg_lines_per_enquiry: rows.length > 0 ? round(lines / rows.length, 1) : 0,
      enquiries_won: won.length,
      lines_won: linesWon,
      // Both rates, side by side, on purpose: a large gap between them means enquiry SIZE rather
      // than effort is driving the numbers. Dropping either column hides exactly that.
      win_rate_enquiries: rate(won.length, rows.length),
      win_rate_lines: rate(linesWon, lines),
      open_enquiries: open.length,
      open_lines: open.reduce((s, e) => s + e.lines, 0),
      avg_cycle_days:
        cycles.length > 0 ? round(cycles.reduce((s, d) => s + d, 0) / cycles.length, 1) : 0,
    }
  })

  return { purchasers: purchasers.sort((a, b) => b.lines - a.lines) }
}

/* ------------------------------------------------------------------ *
 * Conversion breakdown
 * ------------------------------------------------------------------ */

/**
 * Line-count buckets, inclusive on both bounds.
 *
 * Starts at 0, not 1: three of the 23 live enquiries have NO lines at all (verified 2026-08-09),
 * and a `1-5` floor would drop an eighth of the board on the floor without saying so. `max: null`
 * marks the open-ended top bucket, so an enquiry of exactly 100 lines lands in `51-100` and the
 * top bucket is honestly labelled `101+`.
 */
export const SIZE_BUCKETS: ReadonlyArray<{ bucket: string; min: number; max: number | null }> = [
  { bucket: '0', min: 0, max: 0 },
  { bucket: '1-5', min: 1, max: 5 },
  { bucket: '6-20', min: 6, max: 20 },
  { bucket: '21-50', min: 21, max: 50 },
  { bucket: '51-100', min: 51, max: 100 },
  { bucket: '101+', min: 101, max: null },
]

/** Shared cohort arithmetic. `won` counts enquiries whose CURRENT status is `Won` — the same
 *  definition `get_pipeline_analytics` uses, so the KPI tiles and these tables cannot disagree. */
function cohort(rows: readonly EnquiryFact[]) {
  const won = rows.filter((e) => e.status === 'Won')
  const cycles = rows.map((e) => e.cycle_days).filter((d): d is number => d !== null)
  return {
    enquiries: rows.length,
    lines: rows.reduce((s, e) => s + e.lines, 0),
    won: won.length,
    win_rate: rate(won.length, rows.length),
    avg_cycle_days:
      cycles.length > 0 ? round(cycles.reduce((s, d) => s + d, 0) / cycles.length, 1) : 0,
  }
}

/**
 * Win rate by cohort — line-count bucket, customer, and enquiry type.
 *
 * `limit` caps `by_customer` only (the long tail of one-enquiry customers tells you nothing);
 * size buckets and types are small, fixed sets and are never truncated.
 */
export function buildConversionBreakdown(
  enquiries: readonly EnquiryFact[],
  limit = 10,
): ConversionBreakdown {
  const by_size: SizeBucket[] = SIZE_BUCKETS.map((b) => {
    const rows = enquiries.filter(
      (e) => e.lines >= b.min && (b.max === null || e.lines <= b.max),
    )
    const c = cohort(rows)
    return {
      bucket: b.bucket,
      min: b.min,
      max: b.max,
      enquiries: c.enquiries,
      won: c.won,
      win_rate: c.win_rate,
      avg_cycle_days: c.avg_cycle_days,
    }
  })

  const group = <K extends 'customer' | 'enquiry_type'>(key: K) => {
    const groups = new Map<string, EnquiryFact[]>()
    for (const e of enquiries) {
      const k = e[key]
      const list = groups.get(k)
      if (list) list.push(e)
      else groups.set(k, [e])
    }
    return groups
  }

  const by_customer: CustomerConversion[] = [...group('customer')]
    .map(([customer, rows]) => {
      const c = cohort(rows)
      return {
        // IDENTITY. On live, `Customer` is named by field so this equals the label — but it is
        // never trimmed or normalised: live holds `"3MUSHROOMS MARINE  WLL"` with a double space,
        // and tidying the string would break the key it is being used as.
        customer,
        customer_name: rows[0].customer_name || customer,
        enquiries: c.enquiries,
        lines: c.lines,
        won: c.won,
        win_rate: c.win_rate,
      }
    })
    .sort((a, b) => b.enquiries - a.enquiries)
    .slice(0, limit)

  const by_type: TypeConversion[] = [...group('enquiry_type')]
    .map(([enquiry_type, rows]) => {
      const c = cohort(rows)
      return {
        enquiry_type,
        enquiries: c.enquiries,
        lines: c.lines,
        won: c.won,
        win_rate: c.win_rate,
      }
    })
    .sort((a, b) => b.enquiries - a.enquiries)

  return { by_size, by_customer, by_type }
}
